import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Grid, Box } from "@mui/material";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { formatDate } from "../utils/formatters";
import moment from "moment";
import AddDeviceDialog from "../components/Dialogs/addDevice";

// Dashboard components
import HomeHeader from "../components/Dashboard/HomeHeader";
import DeviceStatsGrid from "../components/Dashboard/DeviceStatsGrid";
import RecentAlertsPanel from "../components/Dashboard/RecentAlertsPanel";
import QuickActionsPanel from "../components/Dashboard/QuickActionsPanel";
import LowBatteryPanel from "../components/Dashboard/LowBatteryPanel";

// TanStack Query hooks
import { useDevices } from "../hooks/queries/useDevices";
import { useOrganisationAlerts } from "../hooks/queries/useAlerts";
import { useOrganisationRoles } from "../hooks/queries/useOrganisations";
import { getRefreshInterval } from "../utils/refreshInterval";

// Custom hooks
import {
  useDeviceStats,
  useRecentAlerts,
  useCanAddDevice,
  useIsDataLoaded,
} from "../hooks/useHomeData";
import { useBillingCheck } from "../hooks/useBillingCheck";
import { UtilityService } from "../services/UtilityService";
import { OrganisationService } from "../services/OrganisationService";
import { DataService } from "../services/DataService";

function Home() {
  const { t } = useTranslation("common");
  const { user } = useAuth0();
  const navigate = useNavigate();

  // Check billing status on page load
  useBillingCheck();

  // Redux state (keeping these for now)
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const userDB = useSelector((state) => state.user);

  // Fetch organisation roles using TanStack Query (this is the correct data source)
  const { data: organisationRoles = [] } = useOrganisationRoles(
    selectedOrganisation?.id
  );

  // Ensure organisationRoles is always an array
  const safeUserOrganisationRoles = Array.isArray(organisationRoles)
    ? organisationRoles
    : [];

  // TanStack Query data (useDevices handles refreshInterval internally)
  const { data: devices = [] } = useDevices(selectedOrganisation?.id);

  // Get user refresh rate from localStorage for alert history
  const refreshInterval = getRefreshInterval();
  const {
    data: organisationAlerts = [],
    isLoading: alertsLoading,
    isFetching: alertsFetching,
  } = useOrganisationAlerts(selectedOrganisation?.id, refreshInterval);

  // Local state
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);

  // Custom hooks for computed values
  const deviceStats = useDeviceStats(devices);
  const recentAlerts = useRecentAlerts(organisationAlerts, devices);
  const canAddDevice = useCanAddDevice(
    selectedOrganisation,
    userDB,
    safeUserOrganisationRoles,
    OrganisationService.hasPermission
  );
  const isDataLoaded = useIsDataLoaded(devices, organisationAlerts);

  // Handle device dialog close
  const handleDeviceDialogClose = useCallback(() => {
    setAddDeviceOpen(false);
    // Note: handleRefresh will be called manually when dialog closes
  }, []);

  useEffect(() => {
    UtilityService.setTitle("Home");
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (user?.email && user?.sub) {
      await DataService.refreshData(user.email, user.sub);
      UtilityService.updateSnackbarMessage(
        t("common.refreshed") + " " + formatDate(moment())
      );
    }
  }, [user, t]);

  // Handle device navigation
  const handleDeviceClick = useCallback(
    (deviceId) => {
      navigate(`/device/${deviceId}`);
    },
    [navigate]
  );

  // Handle add device click
  const handleAddDeviceClick = useCallback(() => {
    setAddDeviceOpen(true);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Header */}
      <HomeHeader
        selectedOrganisation={selectedOrganisation}
        onRefresh={handleRefresh}
        userName={user?.name}
      />

      {/* Stats Cards */}
      <DeviceStatsGrid
        devices={devices}
        userDB={userDB}
        deviceStats={deviceStats}
      />

      {/* Main Content Grid */}
      <Grid container spacing={2}>
        {/* Recent Activity - Takes more space */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <RecentAlertsPanel
            recentAlerts={recentAlerts}
            isDataLoaded={isDataLoaded}
            loading={alertsLoading}
            refreshing={alertsFetching && !alertsLoading}
            onDeviceClick={handleDeviceClick}
          />
        </Grid>

        {/* Quick Actions Sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <QuickActionsPanel
            canAddDevice={canAddDevice}
            userDB={userDB}
            onAddDeviceClick={handleAddDeviceClick}
          />
        </Grid>
      </Grid>

      {/* Low Battery Panel - Full width */}
      <LowBatteryPanel devices={devices} />

      <AddDeviceDialog
        open={addDeviceOpen}
        handleClose={handleDeviceDialogClose}
      />
    </Box>
  );
}

export default withAuthenticationRequired(Home);
